import { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import Logo from './Logo';
import { Settings as SidebarSettings, UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import {
  Sidebar,
  sidebarConfig,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarSpace,
  SidebarDivider,
  useSidebarOpenState,
  Link,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import CategoryIcon from '@material-ui/icons/Category';
import LiveHelpIcon from '@material-ui/icons/LiveHelp';
import MyLocationIcon from '@material-ui/icons/MyLocation';
import GroupIcon from '@material-ui/icons/People';
import BuildIcon from '@material-ui/icons/Build';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import GpsNotFixed from '@material-ui/icons/GpsNotFixed';
import GavelIcon from '@material-ui/icons/Gavel';
import SchoolIcon from '@material-ui/icons/School';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: 2 * sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        <Logo knockout version={isOpen ? 'full' : 'icon'} />
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarItem icon={SearchIcon} to="search" text="Search" />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <MyGroupsSidebarItem singularTitle="My Team" pluralTitle="My Teams" icon={GroupIcon} />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Templates" />
        <SidebarItem icon={LiveHelpIcon} to="qeta" text="Q&A" />
        <SidebarItem icon={MyLocationIcon} to="tech-radar" text="Tech Radar" />
        <SidebarItem icon={ExtensionIcon} to="/opsgenie" text="OpsGenie" />
        <SidebarItem
          icon={AccountTreeIcon}
          to="/catalog-graph?rootEntityRefs%5B%5D=group%3Adefault%2Frdc&maxDepth=1&selectedKinds%5B%5D=group&selectedRelations%5B%5D=childOf&selectedRelations%5B%5D=memberOf&selectedRelations%5B%5D=parentOf&selectedRelations%5B%5D=hasMember&unidirectional=true&mergeRelations=true&direction=LR&showFilters=true&curve=curveMonotoneX"
          text="Org Structure"
        />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarItem icon={GpsNotFixed} to="/unregistered-repos" text="Unregistered Repositories" />
        <SidebarDivider />
        <SidebarItem icon={LibraryBooks} to="docs" text="Tech Docs" />
        <SidebarItem icon={GavelIcon} to="docs/default/component/standards-and-decisions" text="Standards" />
        <SidebarItem icon={SchoolIcon} to="docs/default/component/onboarding-for-rdc" text="Onboarding" />
      </SidebarGroup>
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarItem icon={InfoIcon} to="docs/default/component/developer-portal-web" text="About DevPortal" />
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <NotificationsSidebarItem webNotificationsEnabled />
      <SidebarDivider />
      <SidebarGroup label="Settings" icon={<UserSettingsSignInAvatar />} to="/settings">
        <SidebarItem text="DevTools" icon={BuildIcon}>
          <SidebarSubmenu title="DevTools">
            <SidebarSubmenuItem to="devtools/info" title="App Info" />
            <SidebarSubmenuItem to="devtools/unprocessed-entities" title="Unprocessed Entities" />
            <SidebarSubmenuItem to="entity-validation" title="Entity Validator" />
          </SidebarSubmenu>
        </SidebarItem>
        <SidebarSettings />
      </SidebarGroup>
    </Sidebar>
    {children}
  </SidebarPage>
);
